import React, { useState, useEffect } from "react";

// Live pricing from your Google Sheet
const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQcxpxFkXr6qtrXlK717MiKcQbJ-F_8OQf661WfMmQZtkG5pqWNxhKwvEpiax4HU0vlsb8BCv1N9KiL/pub?gid=0&single=true&output=csv";

// Local fallback if Sheet is unavailable
const DEFAULT_PRICING = [
  {
    key: "cap",
    name: "SmartPay Cap",
    pricePerGallon: 3.99,
    capFee: 239.88,
    months: 12,
    desc: "Monthly budget payments with price cap protection. Cap fee included in monthly payment.",
    features: ["Cap Protection", "Automatic Delivery", "Monthly Payments"],
    color: "#FFC107"
  },
  {
    key: "fixed",
    name: "Fixed Price Budget",
    pricePerGallon: 4.09,
    capFee: 0,
    months: 10,
    desc: "Lock in a fixed rate for the season. No cap fee. Paid over 10 months.",
    features: ["Fixed Price", "Automatic Delivery", "Monthly Payments"],
    color: "#0288D1"
  },
  {
    key: "regular",
    name: "Regular Budget",
    pricePerGallon: 4.19,
    capFee: 0,
    months: 10,
    desc: "Pay as delivered, or set a monthly budget. No price protection. 10 monthly payments.",
    features: ["Lowest Price", "Flexible Delivery", "Monthly Payments"],
    color: "#E53935"
  },
  {
    key: "prepay",
    name: "Safeguard Prepay",
    pricePerGallon: 3.89,
    capFee: 0,
    months: 1,
    desc: "Pay in full up front for the best rate. No cap fee. One-time payment.",
    features: ["Prepay", "Best Rate", "Automatic Delivery"],
    color: "#8BC34A"
  },
  {
    key: "retail",
    name: "Retail/Competitor",
    pricePerGallon: 4.29,
    capFee: 0,
    months: 1,
    desc: "Typical current market price in your area.",
    features: ["Market Rate"],
    color: "#BDBDBD"
  }
];

const QUESTIONS = [
  {
    text: "What's your #1 priority for your home heating plan?",
    options: [
      { label: "Predictable monthly payments", value: "predictable", icon: "📅" },
      { label: "Lowest possible price", value: "lowest", icon: "💲" },
      { label: "Full protection from price spikes", value: "protection", icon: "🛡️" },
      { label: "Flexibility with how/when I pay", value: "flexibility", icon: "🔄" }
    ]
  },
  // Gallons handled separately via slider
  {
    text: "How many gallons do you expect to use this season?",
    type: "slider"
  },
  {
    text: "How do you want to pay for your fuel?",
    options: [
      { label: "Monthly budget payments", value: "monthly", icon: "💳" },
      { label: "Pay up front (prepay)", value: "prepay", icon: "💼" },
      { label: "Pay as delivered", value: "ondemand", icon: "🚚" }
    ]
  },
  {
    text: "How important is avoiding price increases during the heating season?",
    type: "slider2"
  },
  {
    text: "Do you want automatic delivery, or will you call when you need oil?",
    options: [
      { label: "Automatic delivery (never worry about running out)", value: "auto", icon: "🚚" },
      { label: "I'll call as needed (more control)", value: "manual", icon: "📞" }
    ]
  }
];

const FAQS = [
  {
    q: "What is a cap fee?",
    a: "The cap fee is a one-time annual protection fee that gets divided into your monthly payments. It helps keep your rate from exceeding a set maximum, but lets you pay less if prices fall."
  },
  {
    q: "Why do some plans use 10 months and others 12?",
    a: "The Cap plan spreads your payments over 12 months for easier budgeting. Fixed and Regular Budget plans are paid over 10 months, matching most heating seasons."
  }
];

function useDynamicPricing(sheetUrl) {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  useEffect(() => {
    if (!sheetUrl) return;
    fetch(sheetUrl)
      .then(res => res.text())
      .then(text => {
        const rows = text.trim().split('\n').map(r => r.split(','));
        const headers = rows[0];
        const plans = rows.slice(1).map(cols => {
          let obj = {};
          headers.forEach((h, i) => obj[h.trim()] = cols[i]);
          obj.pricePerGallon = parseFloat(obj.pricePerGallon);
          obj.capFee = parseFloat(obj.capFee);
          obj.months = parseInt(obj.months, 10);
          return obj;
        });
        setPricing(plans);
      })
      .catch(() => setPricing(DEFAULT_PRICING));
  }, [sheetUrl]);

  return pricing;
}

function recommendPlan(answers, pricing, gallons, priceStability) {
  if (!answers || answers.length < 5) return pricing[0].key;
  if (answers[2] === "prepay") return "prepay";
  if (answers[0] === "predictable" && priceStability >= 4) return "fixed";
  if (answers[0] === "protection" && priceStability >= 3) return "cap";
  if (answers[0] === "lowest") return "regular";
  if (answers[4] === "manual") return "regular";
  return "cap";
}

function getPlanNumbers(plan, gallons) {
  const totalFuel = plan.pricePerGallon * gallons;
  const total = totalFuel + (plan.capFee || 0);
  const months = plan.months || 1;
  const monthly = total / months;
  return {
    total: total,
    monthly: monthly,
    months: months,
    capFeeMonthly: plan.capFee ? plan.capFee / months : 0
  };
}

export default function App() {
  const pricing = useDynamicPricing(SHEET_CSV_URL);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([null, 800, null, 3, null]); // default gallons and price stability
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showFaq, setShowFaq] = useState(false);

  const gallons = answers[1];
  const priceStability = answers[3];

  const retailPlan = pricing.find(p => p.key === "retail") || DEFAULT_PRICING[4];

  const recommendedKey =
    step >= QUESTIONS.length
      ? recommendPlan(answers, pricing, gallons, priceStability)
      : null;

  const recommendedPlan =
    pricing.find(p => p.key === recommendedKey) || pricing[0];

  function planRow(plan) {
    const numbers = getPlanNumbers(plan, gallons);
    return {
      ...plan,
      total: numbers.total,
      monthly: numbers.monthly,
      months: numbers.months,
      capFeeMonthly: numbers.capFeeMonthly,
      savings:
        retailPlan && plan.key !== "retail"
          ? retailPlan.pricePerGallon * gallons - numbers.total
          : 0
    };
  }

  return (
    <div
      style={{
        maxWidth: 620,
        margin: "48px auto",
        padding: 28,
        borderRadius: 24,
        boxShadow: "0 4px 32px #0002",
        background: "#fff"
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <img
          src="logo.png"
          alt="Fox Fuel Logo"
          style={{ height: 48, marginRight: 14 }}
        />
        <div style={{ fontWeight: 900, fontSize: 26, color: "#E53935" }}>
          2025–26 Price Protection Selector
        </div>
      </div>
      {/* INTRO */}
      {step === 0 && (
        <div
          style={{
            marginBottom: 18,
            marginTop: 12,
            fontSize: 20,
            color: "#0288D1"
          }}
        >
          <b>Find your best heating oil plan in under 60 seconds!</b>
          <div style={{ marginTop: 8, color: "#333" }}>
            Answer a few quick questions for an instant, transparent savings analysis.
          </div>
        </div>
      )}
      {/* PROGRESS */}
      <div
        style={{
          height: 12,
          borderRadius: 8,
          background: "#F5F5F5",
          margin: "28px 0 30px 0",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${(step / QUESTIONS.length) * 100}%`,
            background: `linear-gradient(90deg, #E53935, #FFC107)`,
            transition: "width 0.5s cubic-bezier(.52,1.64,.37,.66)"
          }}
        />
      </div>
      {/* QUIZ FLOW */}
      {step < QUESTIONS.length && (
        <>
          {/* Handle slider for gallons */}
          {QUESTIONS[step].type === "slider" ? (
            <div style={{ margin: "40px 0" }}>
              <label style={{ fontWeight: 600, fontSize: 22 }}>
                How many gallons do you expect to use this season?
                <input
                  type="range"
                  min={100}
                  max={2000}
                  step={10}
                  value={gallons}
                  onChange={e => {
                    const val = Number(e.target.value);
                    const newAnswers = [...answers];
                    newAnswers[1] = val;
                    setAnswers(newAnswers);
                  }}
                  style={{ width: "100%", marginTop: 22 }}
                />
                <div style={{ fontSize: 28, marginTop: 18, color: "#0288D1" }}>
                  {gallons} gallons
                </div>
                <div style={{ fontSize: 14, color: "#888", marginTop: 5 }}>
                  Not sure? Most 3-bedroom homes use 500–900 gallons per year.
                </div>
              </label>
              <div style={{ textAlign: "center", marginTop: 30 }}>
                <button
                  style={{
                    background: "#0288D1",
                    color: "#fff",
                    fontWeight: 500,
                    border: "none",
                    borderRadius: 10,
                    padding: "14px 32px",
                    fontSize: 20,
                    cursor: "pointer"
                  }}
                  onClick={() => setStep(step + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          ) : QUESTIONS[step].type === "slider2" ? (
            <div style={{ margin: "40px 0" }}>
              <label style={{ fontWeight: 600, fontSize: 22 }}>
                How important is avoiding price increases during the heating season?
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={priceStability}
                  onChange={e => {
                    const val = Number(e.target.value);
                    const newAnswers = [...answers];
                    newAnswers[3] = val;
                    setAnswers(newAnswers);
                  }}
                  style={{ width: "100%", marginTop: 22 }}
                />
                <div style={{ fontSize: 20, marginTop: 16, color: "#0288D1" }}>
                  {["I’m okay with price changes", "", "Moderate", "", "I want a guaranteed price"][priceStability - 1]}
                </div>
              </label>
              <div style={{ textAlign: "center", marginTop: 30 }}>
                <button
                  style={{
                    background: "#0288D1",
                    color: "#fff",
                    fontWeight: 500,
                    border: "none",
                    borderRadius: 10,
                    padding: "14px 32px",
                    fontSize: 20,
                    cursor: "pointer"
                  }}
                  onClick={() => setStep(step + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 600,
                  color: "#212121",
                  marginBottom: 32
                }}
              >
                {QUESTIONS[step].text}
              </div>
              <div>
                {QUESTIONS[step].options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      const newAnswers = [...answers];
                      newAnswers[step] = opt.value;
                      setAnswers(newAnswers);
                      setStep(step + 1);
                    }}
                    style={{
                      width: "100%",
                      background: "#0288D1",
                      color: "#fff",
                      fontSize: 20,
                      fontWeight: 500,
                      padding: "18px 0",
                      marginBottom: 20,
                      border: "none",
                      borderRadius: 18,
                      boxShadow: "0 2px 14px #0288d122",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 16
                    }}
                  >
                    <span style={{ fontSize: 30 }}>{opt.icon || ""}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ textAlign: "center", marginTop: 16 }}>
                {step > 0 && (
                  <button
                    style={{
                      background: "#F5F5F5",
                      color: "#0288D1",
                      fontWeight: 500,
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 24px",
                      cursor: "pointer"
                    }}
                    onClick={() => setStep(step - 1)}
                  >
                    Back
                  </button>
                )}
                <button
                  style={{
                    background: "#fff",
                    color: "#E53935",
                    fontWeight: 500,
                    border: "none",
                    borderRadius: 10,
                    padding: "8px 24px",
                    cursor: "pointer",
                    marginLeft: 18
                  }}
                  onClick={() => setShowFaq(!showFaq)}
                >
                  {showFaq ? "Hide FAQ" : "Show FAQ"}
                </button>
              </div>
              {showFaq && (
                <div
                  style={{
                    margin: "30px 0",
                    background: "#F5F5F5",
                    borderRadius: 12,
                    padding: 18
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#0288D1",
                      fontSize: 17
                    }}
                  >
                    FAQs
                  </div>
                  {FAQS.map(f => (
                    <div key={f.q} style={{ margin: "10px 0 0 0" }}>
                      <b>{f.q}</b>
                      <div style={{ marginLeft: 8, fontSize: 15 }}>{f.a}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* RESULTS PAGE */}
      {step === QUESTIONS.length && !showForm && (
        <div>
          <div
            style={{
              fontSize: 25,
              fontWeight: 800,
              color: "#0288D1",
              textAlign: "center",
              margin: "0 0 15px 0"
            }}
          >
            🎉 We Recommend:{" "}
            <span style={{ color: "#E53935" }}>{recommendedPlan.name}!</span>
          </div>
          <div
            style={{
              margin: "0 auto 12px auto",
              maxWidth: 400,
              background: "#F5F5F5",
              borderRadius: 18,
              padding: 16,
              fontSize: 18,
              color: "#212121",
              boxShadow: "0 1px 8px #0288d111"
            }}
          >
            {recommendedPlan.desc}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexWrap: "wrap",
                gap: 8
              }}
            >
              {recommendedPlan.features &&
                recommendedPlan.features.map(feat => (
                  <span
                    key={feat}
                    style={{
                      background: recommendedPlan.color || "#FFC107",
                      color: "#E53935",
                      fontWeight: 700,
                      borderRadius: 12,
                      padding: "4px 12px",
                      fontSize: 15
                    }}
                  >
                    {feat}
                  </span>
                ))}
            </div>
            <div
              style={{
                marginTop: 12,
                fontSize: 16,
                color: "#0288D1"
              }}
            >
              Why this plan? Because you chose {answers[0] && QUESTIONS[0].options.find(o => o.value === answers[0]).label}, want to use {gallons} gallons, and prefer {answers[2] && QUESTIONS[2].options.find(o => o.value === answers[2]).label} payments.
            </div>
          </div>
          {/* Comparison Table (Card Layout for Responsive UX) */}
          <details style={{ margin: "20px 0" }}>
            <summary style={{ fontWeight: 600, color: "#0288D1", cursor: "pointer", fontSize: 18 }}>
              See all plans compared ↓
            </summary>
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 16 }}>
              {pricing.filter(plan => plan.key !== "retail").map(plan => {
                const row = planRow(plan);
                return (
                  <div key={plan.key} style={{
                    background: recommendedPlan.key === plan.key ? "#FFC107" : "#F5F5F5",
                    color: recommendedPlan.key === plan.key ? "#E53935" : "#212121",
                    fontWeight: recommendedPlan.key === plan.key ? 700 : 400,
                    borderRadius: 12,
                    padding: 16,
                    minWidth: 210,
                    maxWidth: 280,
                    marginBottom: 6,
                    boxShadow: "0 2px 12px #0288d111"
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 17 }}>{plan.name}</div>
                    <div style={{ margin: "6px 0", fontSize: 15 }}>{plan.desc}</div>
                    <div><b>Est. total:</b> {row.total.toLocaleString(undefined, { style: "currency", currency: "USD" })}</div>
                    <div><b>Monthly:</b> {row.monthly.toLocaleString(undefined, { style: "currency", currency: "USD" })} ({row.months} mo)</div>
                    {row.capFeeMonthly > 0 && (
                      <div style={{ fontSize: 13, color: "#0288D1" }}>Includes protection: {row.capFeeMonthly.toLocaleString(undefined, { style: "currency", currency: "USD" })}/mo</div>
                    )}
                    <div style={{ fontSize: 13, color: "#0288D1" }}>{(plan.features || []).join(", ")}</div>
                    {plan.key !== "retail" && (
                      <div style={{ marginTop: 8, color: "#388e3c", fontWeight: 600 }}>
                        You save {row.savings > 0 ? row.savings.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "$0"} vs. retail
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
          {/* Adjust gallons on results page for interactive savings! */}
          <div style={{ margin: "18px 0", textAlign: "center" }}>
            <label style={{ fontWeight: 500 }}>Adjust gallons:
              <input
                style={{ marginLeft: 8, width: 80, padding: 4, fontSize: 18, borderRadius: 8, border: "1px solid #ccc" }}
                type="number"
                min={1}
                max={3000}
                value={gallons}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  const newAnswers = [...answers];
                  newAnswers[1] = isNaN(val) ? "" : val;
                  setAnswers(newAnswers);
                }}
              />
            </label>
          </div>
          <button
            style={{
              width: "100%",
              margin: "16px 0 0 0",
              background: `linear-gradient(90deg, #E53935, #FFC107)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: 22,
              padding: "18px 0",
              border: "none",
              borderRadius: 18,
              cursor: "pointer",
              boxShadow: "0 4px 24px #e5393533"
            }}
            onClick={() => setShowForm(true)}
          >
            Get My Personalized Quote →
          </button>
        </div>
      )}
      {/* LEAD FORM */}
      {showForm && !submitted && (
        <form
          action="https://formspree.io/f/xknkgwod"
          method="POST"
          style={{ marginTop: 12 }}
          onSubmit={() => setSubmitted(true)}
        >
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#E53935" }}>
            Request Your Quote
          </div>
          <input name="plan" type="hidden" value={recommendedPlan.name || ""} />
          <input name="gallons" type="hidden" value={gallons} />
          <label style={{ fontWeight: 500 }}>Name
            <input name="name" required style={{
              width: "100%", padding: 12, borderRadius: 10, border: "1px solid #eee",
              fontSize: 17, marginTop: 4, marginBottom: 16
            }} />
          </label>
          <label style={{ fontWeight: 500 }}>Email
            <input name="email" type="email" required style={{
              width: "100%", padding: 12, borderRadius: 10, border: "1px solid #eee",
              fontSize: 17, marginTop: 4, marginBottom: 16
            }} />
          </label>
          <label style={{ fontWeight: 500 }}>Phone
            <input name="phone" type="tel" required style={{
              width: "100%", padding: 12, borderRadius: 10, border: "1px solid #eee",
              fontSize: 17, marginTop: 4, marginBottom: 16
            }} />
          </label>
          <button
            style={{
              width: "100%",
              background: "#0288D1",
              color: "#fff",
              fontWeight: 700,
              fontSize: 20,
              padding: "15px 0",
              border: "none",
              borderRadius: 12,
              cursor: "pointer",
              boxShadow: "0 2px 10px #0288d122"
            }}
            type="submit"
          >Send Quote Request</button>
        </form>
      )}
      {/* CONFIRMATION */}
      {submitted && (
        <div style={{ textAlign: "center", marginTop: 64, color: "#E53935", fontWeight: 700, fontSize: 28 }}>
          ✅ Thank you!
          <div style={{ fontSize: 20, color: "#212121", marginTop: 16 }}>
            Your quote request has been sent.<br />
            Fox Fuel will contact you soon.
          </div>
        </div>
      )}
    </div>
  );
}
